import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, RefreshCw, Loader2, TrendingUp, Users, ShoppingBag,
  Globe, MousePointerClick, Target, Clock,
} from "lucide-react";
import { adminApi } from "../api";

const EUR = (v) => `€${(Number(v) || 0).toLocaleString("pt-PT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const NUM = (v) => (Number(v) || 0).toLocaleString("pt-PT");

const MONTHS = ["Ano inteiro", "Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

function Kpi({ label, value, icon: Icon, accent = "default", testid }) {
  const cls = {
    default: "bg-white border-[#F3F0E7] text-[#1B1A17]",
    ok: "bg-emerald-50 border-emerald-200 text-emerald-900",
    info: "bg-blue-50 border-blue-200 text-blue-900",
    warn: "bg-amber-50 border-amber-200 text-amber-900",
  }[accent];
  return (
    <div data-testid={testid} className={`p-4 rounded-lg border ${cls}`}>
      <div className="flex items-center gap-2 opacity-70">
        {Icon && <Icon size={14} />}
        <p className="text-[10px] uppercase tracking-[0.15em] font-semibold">{label}</p>
      </div>
      <p className="text-2xl font-bold font-mono mt-1">{value}</p>
    </div>
  );
}

function AttrTable({ rows, testidPrefix }) {
  if (!rows?.length) {
    return <p className="text-sm text-[#978D78] py-6 text-center" data-testid={`${testidPrefix}-empty`}>Sem dados de atribuição para este período.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid={`${testidPrefix}-table`}>
        <thead>
          <tr className="text-left text-[#978D78] border-b border-[#F3F0E7]">
            <th className="py-2 pr-3 font-semibold">Origem</th>
            <th className="py-2 pr-3 font-semibold">Campanha</th>
            <th className="py-2 pr-3 font-semibold text-right">Leads</th>
            <th className="py-2 pr-3 font-semibold text-right">C/ compra</th>
            <th className="py-2 pr-3 font-semibold text-right">Receita</th>
            <th className="py-2 font-semibold text-right">Receita/lead</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.origem}-${r.campanha}-${i}`} className="border-b border-[#F8F6F0] hover:bg-[#FBFAF6]" data-testid={`${testidPrefix}-row-${i}`}>
              <td className="py-2 pr-3">
                <span className="inline-flex items-center px-2 py-0.5 rounded bg-[#0F1829] text-white text-xs font-medium capitalize">{r.origem}</span>
              </td>
              <td className="py-2 pr-3 text-[#1B1A17]">{r.campanha}</td>
              <td className="py-2 pr-3 text-right font-mono">{NUM(r.leads)}</td>
              <td className="py-2 pr-3 text-right font-mono">{NUM(r.clientes_com_compra)}</td>
              <td className="py-2 pr-3 text-right font-mono font-semibold">{EUR(r.receita)}</td>
              <td className="py-2 text-right font-mono text-[#6B7280]">{EUR(r.receita_por_lead)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminAttribution() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(0);
  const [touch, setTouch] = useState("first");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rebuilding, setRebuilding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await adminApi.marketingAttribution(year, month);
      setData(d);
    } catch (e) {
      toast.error(e.message || "Erro a carregar atribuição");
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => { load(); }, [load]);

  const rebuild = async () => {
    setRebuilding(true);
    try {
      const r = await adminApi.marketingAttributionRebuild();
      toast.success(`Consolidado: ${r.clientes} clientes, ${r.visitas_total} visitas`);
      await load();
    } catch (e) {
      toast.error(e.message || "Erro a consolidar");
    } finally {
      setRebuilding(false);
    }
  };

  const k = data?.kpis || {};
  const rows = touch === "first" ? data?.first_touch : data?.last_touch;
  const years = [now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2];

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="admin-attribution-page">
      <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-[#978D78] hover:text-[#0F1829] mb-4" data-testid="attr-back">
        <ArrowLeft size={15} /> Voltar
      </Link>

      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#0F1829] flex items-center gap-2">
            <Target size={22} /> Atribuição de Marketing
          </h1>
          <p className="text-sm text-[#978D78] mt-1">Visitas WordPress → leads → receita, por origem e campanha (first e last touch).</p>
          {data?.atualizado && (
            <p className="text-xs text-[#B7AE98] mt-1 inline-flex items-center gap-1">
              <Clock size={11} /> Última consolidação: {new Date(data.atualizado).toLocaleString("pt-PT")}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="attr-year"
            className="px-3 py-2 text-sm border border-[#E6E1D6] rounded bg-white outline-none focus:border-[#0F1829]">
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <select value={month} onChange={(e) => setMonth(Number(e.target.value))} data-testid="attr-month"
            className="px-3 py-2 text-sm border border-[#E6E1D6] rounded bg-white outline-none focus:border-[#0F1829]">
            {MONTHS.map((m, i) => <option key={i} value={i}>{m}</option>)}
          </select>
          <button onClick={rebuild} disabled={rebuilding} data-testid="attr-rebuild"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded bg-[#0F1829] text-white hover:bg-[#1a2740] disabled:opacity-50">
            {rebuilding ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Consolidar agora
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-[#978D78]" data-testid="attr-loading">
          <Loader2 size={22} className="animate-spin mr-2" /> A carregar…
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <Kpi label="Leads identificados" value={NUM(k.leads)} icon={Users} accent="info" testid="attr-kpi-leads" />
            <Kpi label="Clientes c/ compra" value={NUM(k.clientes_com_compra)} icon={ShoppingBag} accent="ok" testid="attr-kpi-compras" />
            <Kpi label="Receita atribuída" value={EUR(k.receita)} icon={TrendingUp} accent="ok" testid="attr-kpi-receita" />
            <Kpi label="Receita / lead" value={EUR(k.receita_por_lead)} icon={Target} testid="attr-kpi-rpl" />
            <Kpi label="Visitas totais" value={NUM(k.visitas_total)} icon={Globe} testid="attr-kpi-visitas" />
            <Kpi label="Visitas anónimas" value={NUM(k.visitas_anonimas)} icon={MousePointerClick} accent="warn" testid="attr-kpi-anon" />
            <Kpi label="Visitas identificadas" value={NUM(k.visitas_identificadas)} icon={Users} testid="attr-kpi-ident" />
            <Kpi label="Visitantes únicos" value={NUM(k.visitantes_unicos)} icon={Users} testid="attr-kpi-unicos" />
          </div>

          <div className="bg-white border border-[#F3F0E7] rounded-lg p-4 sm:p-5 mb-6">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              <h2 className="text-lg font-semibold text-[#0F1829]">Por origem / campanha</h2>
              <div className="inline-flex rounded-lg border border-[#E6E1D6] overflow-hidden text-sm">
                <button onClick={() => setTouch("first")} data-testid="attr-toggle-first"
                  className={`px-3 py-1.5 ${touch === "first" ? "bg-[#0F1829] text-white" : "bg-white text-[#6B7280] hover:bg-[#F5F2EB]"}`}>
                  First-touch
                </button>
                <button onClick={() => setTouch("last")} data-testid="attr-toggle-last"
                  className={`px-3 py-1.5 ${touch === "last" ? "bg-[#0F1829] text-white" : "bg-white text-[#6B7280] hover:bg-[#F5F2EB]"}`}>
                  Last-touch
                </button>
              </div>
            </div>
            <p className="text-xs text-[#978D78] mb-3">
              {touch === "first"
                ? "First-touch: a PRIMEIRA origem que trouxe o visitante (aquisição)."
                : "Last-touch: a ÚLTIMA origem antes de estar identificado (conversão)."}
            </p>
            <AttrTable rows={rows} testidPrefix={`attr-${touch}`} />
          </div>

          <div className="bg-white border border-[#F3F0E7] rounded-lg p-4 sm:p-5">
            <h2 className="text-lg font-semibold text-[#0F1829] mb-4">Top páginas de entrada (por receita)</h2>
            {data?.top_pages?.length ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="attr-pages-table">
                  <thead>
                    <tr className="text-left text-[#978D78] border-b border-[#F3F0E7]">
                      <th className="py-2 pr-3 font-semibold">Página de entrada</th>
                      <th className="py-2 pr-3 font-semibold text-right">Leads</th>
                      <th className="py-2 pr-3 font-semibold text-right">C/ compra</th>
                      <th className="py-2 font-semibold text-right">Receita</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.top_pages.map((p, i) => (
                      <tr key={`${p.pagina}-${i}`} className="border-b border-[#F8F6F0] hover:bg-[#FBFAF6]" data-testid={`attr-page-row-${i}`}>
                        <td className="py-2 pr-3 text-[#1B1A17] truncate max-w-[420px]" title={p.pagina}>{p.pagina}</td>
                        <td className="py-2 pr-3 text-right font-mono">{NUM(p.leads)}</td>
                        <td className="py-2 pr-3 text-right font-mono">{NUM(p.clientes_com_compra)}</td>
                        <td className="py-2 text-right font-mono font-semibold">{EUR(p.receita)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-[#978D78] py-6 text-center" data-testid="attr-pages-empty">Sem páginas com receita atribuída.</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
